import { ContentChip, isoDateStringToLocalFormat, LoadingSkeleton, Modal, tokens } from 'ComponentLibrary'
import { ColumnDef, createColumnHelper, OnChangeFn, RowSelectionState } from '@tanstack/react-table'
import { Box, CircularProgress, Stack, Tooltip } from '@mui/material'
import { DownloadSimple as Download, Trash } from '@phosphor-icons/react'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { useState } from 'react'
import { SimpleTable } from 'ComponentLibrary/src/piazza/tables'
import { SimpleTableCell } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableCell'
import { SimpleTableHeadline } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableHeadline'
import { SimpleTableTextCell } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableTextCells'
import type { Document, DocumentWithIndex } from 'ComponentLibrary/src/molecules/FileUploadModal/types'

const DOCUMENT_NAME_COLUMN_WIDTH = 240
const DOCUMENT_DATE_COLUMN_WIDTH = 120
const DOCUMENT_ACTIONS_COLUMN_WIDTH = 80
const DOCUMENT_SINGLE_ACTION_COLUMN_WIDTH = 48
const DOCUMENT_SELECTION_COLUMN_WIDTH = 40

type DocumentTableCopy = {
    nameHeader: string
    tagsHeader: string
    dateHeader: string
    downloadAction: string
    deleteAction: string
    empty: string
    emptyFiltered: string
    deleteModalTitle: string
    deleteModalPrimaryAction: string
    deleteModalSecondaryAction: string
    deleteModalDescription: string
    selectAllAction: string
    selectAction: string
}

const DEFAULT_DOCUMENT_TABLE_COPY: DocumentTableCopy = {
    nameHeader: 'Name',
    tagsHeader: 'Tags',
    dateHeader: 'Datum',
    downloadAction: 'Dokument herunterladen',
    deleteAction: 'Dokument löschen',
    empty: 'Keine Dokumente vorhanden',
    emptyFiltered: 'Keine Dokumente entsprechen den Filtern',
    deleteModalTitle: 'Dokument löschen',
    deleteModalPrimaryAction: 'Löschen',
    deleteModalSecondaryAction: 'Abbrechen',
    deleteModalDescription: 'Soll das Dokument {{documentName}} wirklich gelöscht werden?',
    selectAllAction: 'Alle Dokumente auswählen',
    selectAction: 'Dokument auswählen',
}

type DocumentUploadTableBaseProps = {
    isUploading?: boolean
    numDocumentsToUpload?: number
    failedDocumentUploads?: string[]
    downloadDocument?: (documentId: string) => void
    orderId?: string
    generateZipUrl?: () => void
    isGeneratingDocumentsZipUrl?: boolean
    openModal?: () => void
    isDownloadingDocument?: boolean
    downloadingDocumentIds?: ReadonlySet<string>
    isFiltered?: boolean
    disableVirtualization?: boolean
    copy?: Partial<DocumentTableCopy>
    locale?: string
}

type DocumentTableSelectionProps =
    | {
          rowSelection: RowSelectionState
          setRowSelection: OnChangeFn<RowSelectionState>
      }
    | {
          rowSelection?: never
          setRowSelection?: never
      }

type DocumentUploadTableProps = DocumentUploadTableBaseProps &
    DocumentTableSelectionProps &
    (
        | {
              deleteDocument: (documentIndex: number) => void
              documents: DocumentWithIndex[]
          }
        | {
              deleteDocument?: never
              documents: Document[]
          }
    )

export const DocumentTable = ({
    documents,
    isUploading,
    numDocumentsToUpload,
    deleteDocument,
    downloadDocument,
    isDownloadingDocument,
    downloadingDocumentIds,
    isFiltered,
    disableVirtualization,
    rowSelection,
    setRowSelection,
    copy,
    locale = 'de-DE',
}: DocumentUploadTableProps) => {
    const columnHelper = createColumnHelper<Document>()
    const [documentToDelete, setDocumentToDelete] = useState<DocumentWithIndex | null>(null)
    const resolvedCopy = { ...DEFAULT_DOCUMENT_TABLE_COPY, ...copy }
    const isSelectionEnabled = rowSelection !== undefined && setRowSelection !== undefined

    const closeDeleteModal = () => setDocumentToDelete(null)
    const confirmDelete = () => {
        if (!deleteDocument || !documentToDelete) {
            return
        }
        deleteDocument(documentToDelete.index)
        closeDeleteModal()
    }

    const columns = [
        ...(isSelectionEnabled
            ? [
                  columnHelper.display({
                      id: 'select',
                      size: DOCUMENT_SELECTION_COLUMN_WIDTH,
                      enableResizing: false,
                      header: ({ table }) => (
                          <SimpleTableCell>
                              <Checkbox
                                  checked={table.getIsAllPageRowsSelected()}
                                  indeterminate={table.getIsSomePageRowsSelected()}
                                  onChange={table.getToggleAllPageRowsSelectedHandler()}
                                  ariaLabel={resolvedCopy.selectAllAction}
                              />
                          </SimpleTableCell>
                      ),
                      cell: ({ row }) => (
                          <SimpleTableCell>
                              <Box onClick={(event) => event.stopPropagation()}>
                                  <Checkbox
                                      checked={row.getIsSelected()}
                                      disabled={!row.getCanSelect()}
                                      onChange={row.getToggleSelectedHandler()}
                                      ariaLabel={`${resolvedCopy.selectAction}: ${row.original.name}`}
                                  />
                              </Box>
                          </SimpleTableCell>
                      ),
                  }),
              ]
            : []),
        columnHelper.accessor('name', {
            header: () => <SimpleTableHeadline variant="secondary" text={resolvedCopy.nameHeader} alignment="left" />,
            size: DOCUMENT_NAME_COLUMN_WIDTH,
            cell: (info) => <SimpleTableTextCell text={info.getValue() ?? '-'} />,
        }),
        columnHelper.accessor('tags', {
            header: () => <SimpleTableHeadline variant="secondary" text={resolvedCopy.tagsHeader} alignment="left" />,
            meta: { fillAvailableWidth: true },
            cell: (info) => {
                const tags = info.getValue() ?? []

                return (
                    <SimpleTableCell>
                        {!!tags.length && (
                            <Tooltip
                                placement="top"
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            ...tokens.Popup,
                                            backgroundColor: tokens.ColorBackgroundLight,
                                        },
                                    },
                                }}
                                title={
                                    <Stack direction="row" flexWrap="wrap" gap={tokens.SizingSpacing075}>
                                        {tags.map((tag, index) => (
                                            <Box key={`${tag.title}-${index}`} flexShrink={0}>
                                                <ContentChip size="small" color={tag.color} label={tag.title} />
                                            </Box>
                                        ))}
                                    </Stack>
                                }
                            >
                                <Box
                                    width="100%"
                                    minWidth={0}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    whiteSpace="nowrap"
                                    sx={{ ...tokens.TextS, color: tokens.ColorTextDefault }}
                                >
                                    {tags.map((tag, index) => (
                                        <Box
                                            key={`${tag.title}-${index}`}
                                            display="inline-flex"
                                            marginRight={index < tags.length - 1 ? tokens.SizingSpacing075 : 0}
                                            sx={{ verticalAlign: 'middle' }}
                                        >
                                            <ContentChip size="small" color={tag.color} label={tag.title} />
                                        </Box>
                                    ))}
                                </Box>
                            </Tooltip>
                        )}
                    </SimpleTableCell>
                )
            },
        }),
        columnHelper.accessor('created_on', {
            header: () => <SimpleTableHeadline variant="secondary" text={resolvedCopy.dateHeader} alignment="left" />,
            size: DOCUMENT_DATE_COLUMN_WIDTH,
            cell: (info) => <SimpleTableTextCell text={isoDateStringToLocalFormat(info.getValue(), false, locale) || '-'} />,
        }),
        columnHelper.accessor('uuid', {
            header: () => <SimpleTableCell />,
            size: downloadDocument && deleteDocument ? DOCUMENT_ACTIONS_COLUMN_WIDTH : DOCUMENT_SINGLE_ACTION_COLUMN_WIDTH,
            cell: (info) => {
                const documentId = info.getValue()
                const isDownloadingThisDocument = downloadingDocumentIds?.has(documentId) ?? false

                return (
                    <SimpleTableCell alignment="right">
                        {downloadDocument && (
                            <IconButton
                                variant="tertiary"
                                size="small"
                                icon={
                                    isDownloadingThisDocument ? (
                                        <CircularProgress size={tokens.SizingIconS} sx={{ position: 'relative', top: 0, left: 0 }} />
                                    ) : (
                                        <Download weight="bold" />
                                    )
                                }
                                onClick={(event) => {
                                    event.stopPropagation()
                                    downloadDocument(documentId)
                                }}
                                disabled={isDownloadingDocument || isDownloadingThisDocument}
                                ariaLabel={resolvedCopy.downloadAction}
                                tooltip={resolvedCopy.downloadAction}
                            />
                        )}
                        {deleteDocument && (
                            <IconButton
                                variant="tertiary"
                                size="small"
                                icon={<Trash weight="bold" />}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    setDocumentToDelete(info.row.original as DocumentWithIndex)
                                }}
                                ariaLabel={resolvedCopy.deleteAction}
                                tooltip={resolvedCopy.deleteAction}
                            />
                        )}
                    </SimpleTableCell>
                )
            },
        }),
    ] as ColumnDef<Document>[]

    return (
        <>
            <Stack direction="column">
                <SimpleTable
                    columns={columns}
                    data={documents}
                    width="100%"
                    controlledSizing
                    disableVirtualization={disableVirtualization}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    getRowId={isSelectionEnabled ? (document) => document.uuid : undefined}
                    isFiltered={isFiltered}
                    copy={{ empty: resolvedCopy.empty, emptyFiltered: resolvedCopy.emptyFiltered }}
                />
                {isUploading && !!numDocumentsToUpload && (
                    <LoadingSkeleton
                        count={numDocumentsToUpload}
                        sx={{ transform: 'none', marginTop: '2px', height: '35px', width: '100%' }}
                    />
                )}
            </Stack>
            <Modal
                title={resolvedCopy.deleteModalTitle}
                isOpen={!!deleteDocument && !!documentToDelete}
                onClose={closeDeleteModal}
                showCloseIcon
                footerProps={{
                    primaryButton: {
                        text: resolvedCopy.deleteModalPrimaryAction,
                        destructive: true,
                        onClick: confirmDelete,
                    },
                    secondaryButton: {
                        text: resolvedCopy.deleteModalSecondaryAction,
                        onClick: closeDeleteModal,
                    },
                }}
            >
                <Stack direction="column" gap={tokens.SizingSpacing200} width="100%">
                    <Typography variant="textM" color={tokens.ColorTextDefault}>
                        {resolvedCopy.deleteModalDescription.split('{{documentName}}')[0]}
                        <span style={{ fontWeight: 500 }}>{documentToDelete?.name}</span>
                        {resolvedCopy.deleteModalDescription.split('{{documentName}}')[1]}
                    </Typography>
                </Stack>
            </Modal>
        </>
    )
}
